import "./labelWorkspace.css";

import Money from "@dashboard/components/Money";
import { Box, Button, Text } from "@saleor/macaw-ui-next";
import { Minus, PackagePlus, Plus, ScanBarcode, Trash2 } from "lucide-react";
import { useIntl } from "react-intl";

import { batchMessages } from "./batchMessages";
import { LabelThumbnail } from "./LabelThumbnail";
import { BatchBadge, LabelVariantHit } from "./LabelVariantSearch";
import { messages } from "./views/LabelPrintingOverview/messages";

export interface LabelQueueEntry extends LabelVariantHit {
  quantity: number;
}

interface LabelQueueProps {
  entries: LabelQueueEntry[];
  // The id of the row just added, briefly highlighted so a scan's effect is
  // visible even when it only bumped an existing row's quantity.
  highlightId: string | null;
  onQuantityChange: (variantId: string, quantity: number) => void;
  onRemove: (variantId: string) => void;
  // Admins register delivery batches straight from the queue.
  onRegisterBatch?: (entry: LabelQueueEntry) => void;
}

export const MAX_LABEL_COPIES = 100;

export const LabelQueue = ({
  entries,
  highlightId,
  onQuantityChange,
  onRemove,
  onRegisterBatch,
}: LabelQueueProps) => {
  const intl = useIntl();

  if (entries.length === 0) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap={2}
        padding={6}
        borderWidth={1}
        borderColor="default1"
        borderRadius={3}
        style={{ borderStyle: "dashed" }}
      >
        <ScanBarcode size={22} aria-hidden />
        <Text size={2} color="default2" textAlign="center">
          {intl.formatMessage(messages.emptyQueue)}
        </Text>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" data-test-id="label-queue">
      {entries.map((entry, index) => (
        <Box
          key={entry.variantId}
          display="grid"
          alignItems="center"
          gap={3}
          paddingY={2}
          borderTopWidth={index === 0 ? 0 : 1}
          borderTopStyle="solid"
          borderColor="default1"
          backgroundColor={entry.variantId === highlightId ? "accent1" : undefined}
          style={{
            gridTemplateColumns: "40px minmax(0, 1fr) auto auto",
            transition: "background-color 0.6s",
          }}
        >
          <LabelThumbnail url={entry.thumbnailUrl} />
          <Box style={{ minWidth: 0 }}>
            <Text size={2}>
              <Text size={2} fontWeight="bold">
                {entry.variantName || intl.formatMessage(messages.productLabel)}
              </Text>
              <Text size={2} color="default2">
                {" · "}
                {entry.productName}
              </Text>
            </Text>
            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
              <Text size={1} color="default2" style={{ fontFamily: "monospace" }}>
                {entry.sku}
              </Text>
              {entry.batchNumber ? (
                <BatchBadge code={entry.batchNumber} />
              ) : (
                <Text
                  size={1}
                  color="warning1"
                  backgroundColor="warning1"
                  paddingX={1.5}
                  borderRadius={2}
                >
                  {intl.formatMessage(batchMessages.noBatchYet)}
                </Text>
              )}
              {entry.price && (
                <Text size={1} color="default2">
                  <Money money={entry.price} />
                </Text>
              )}
              {onRegisterBatch && (
                <Button
                  variant="tertiary"
                  size="small"
                  icon={<PackagePlus size={14} />}
                  onClick={() => onRegisterBatch(entry)}
                >
                  {intl.formatMessage(
                    entry.batchNumber
                      ? batchMessages.registerNewBatch
                      : batchMessages.registerBatch,
                  )}
                </Button>
              )}
            </Box>
          </Box>
          <Box
            display="flex"
            alignItems="center"
            borderWidth={1}
            borderStyle="solid"
            borderColor="default1"
            borderRadius={3}
            overflow="hidden"
          >
            <Button
              variant="tertiary"
              size="small"
              icon={<Minus size={14} />}
              aria-label={intl.formatMessage(messages.decrease)}
              disabled={entry.quantity <= 1}
              onClick={() => onQuantityChange(entry.variantId, entry.quantity - 1)}
            />
            <input
              className="labelQtyInput"
              type="number"
              min={1}
              max={MAX_LABEL_COPIES}
              aria-label={`${intl.formatMessage(messages.quantityLabel)} — ${entry.sku}`}
              value={entry.quantity}
              onChange={event => onQuantityChange(entry.variantId, Number(event.target.value) || 1)}
            />
            <Button
              variant="tertiary"
              size="small"
              icon={<Plus size={14} />}
              aria-label={intl.formatMessage(messages.increase)}
              disabled={entry.quantity >= MAX_LABEL_COPIES}
              onClick={() => onQuantityChange(entry.variantId, entry.quantity + 1)}
            />
          </Box>
          <Button
            variant="tertiary"
            size="small"
            icon={<Trash2 size={16} />}
            aria-label={intl.formatMessage(messages.remove, {
              sku: entry.sku || entry.productName,
            })}
            title={intl.formatMessage(messages.remove, { sku: entry.sku || entry.productName })}
            onClick={() => onRemove(entry.variantId)}
          />
        </Box>
      ))}
    </Box>
  );
};
